import * as Sentry from "@sentry/nextjs";
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  signInWithPopup,
} from "firebase/auth";
import { action, makeAutoObservable, reaction, runInAction } from "mobx";

import { getCurrentUser, sendSignInLink, updateCurrentUser } from "@/api";
import { subscribe } from "@/api/cable";
import { getApiErrorMessage, isApiError } from "@/api/client";
import { getUrlOrigin } from "@/urls";
import { firebaseAuth } from "@/utils/firebaseAuth";
import { setLoggedInCookie } from "@/utils/loggedInCookie";
import { isInsideMobileWebView } from "@/utils/mobileBridgeAuth";
import { signInLinkTrackingParams } from "@/utils/posthog/signInLink";

import { canSignInWithRedirect } from "./canSignInWithRedirect";

import type { signInProvider } from "./authTypes";
import type { CurrentUser, UpdateCurrentUserBody } from "@/api";
import type { RootStore } from "@/store/RootStore";
import type { UserData } from "@/store/user/userTypes";
import type { Channel } from "@anycable/core";
import type { User as FirebaseUser, OAuthCredential, UserCredential } from "firebase/auth";

type AuthStatus = "idle" | "loading" | "ready" | "error";

const googleProvider = new GoogleAuthProvider();

export class AuthModel {
  fbUser: FirebaseUser | null = null;

  oauthCredential: OAuthCredential | null = null;

  userData?: UserData | null = undefined;

  /**
   * The Firebase identity's email is verified on another account (409 from
   * GET /users/current): no Corca account exists for this session and none
   * can be created — the user must sign in with the owning account.
   */
  emailClaimed: boolean = false;

  status: AuthStatus = "idle";

  error: string = "";

  rootStore?: RootStore;

  userChannel?: Channel;

  redirectResultPromise?: Promise<UserCredential | null>;

  private markAuthReady: (() => void)[] = [];

  /**
   * Used in SSR to prerender logged in state.
   * Note: do NOT rely on it as a guaranteed user state.
   */
  private serverLoggedIn: boolean = false;

  get currentUser(): UserData | undefined | null {
    return this.userData;
  }

  get isLoggedIn(): boolean {
    return typeof this.userData === "undefined" ? this.serverLoggedIn : !!this.userData;
  }

  get authTokenResolved(): boolean {
    return this.status === "ready" || this.fbUser !== null;
  }

  async getRedirectResult(): Promise<UserCredential | null> {
    if (!this.redirectResultPromise) {
      this.redirectResultPromise = getRedirectResult(firebaseAuth);
    }
    return this.redirectResultPromise;
  }

  constructor({ serverLoggedIn = false }: { serverLoggedIn?: boolean } = {}) {
    this.serverLoggedIn = serverLoggedIn;
    makeAutoObservable(this);
  }

  resetUser(): void {
    this.fbUser = null;
    this.oauthCredential = null;
    this.userData = null;
    this.emailClaimed = false;
    this.rootStore?.doc?.api.refetch();
    this.redirectResultPromise = undefined;
  }

  subscribeToAuthChanges(): () => void {
    this.setStatus("loading");

    const disposeUserChannel = reaction(
      () => this.userData?.uid,
      (uid) => {
        this.userChannel?.disconnect();
        this.userChannel = uid ? subscribe("UserChannel", { user_id: uid }) : undefined;
      },
      { fireImmediately: true },
    );

    // In the app WebView, auth is the native bridge token, not a Firebase SDK
    // session — `onAuthStateChanged` only ever reports null. Bootstrap the user
    // profile directly via the bridge-authenticated API so `currentUser` is set.
    if (isInsideMobileWebView()) {
      this.checkExistingUser();
    }

    const unsubscribe = firebaseAuth.onAuthStateChanged(
      action(async (fbUser: FirebaseUser | null) => {
        if (!fbUser) {
          if (isInsideMobileWebView()) {
            this.setStatus("ready");
            return;
          }
          this.resetUser();
          this.setStatus("ready");
          setLoggedInCookie(false);
          this.serverLoggedIn = false;
          return;
        }

        setLoggedInCookie(true);
        this.fbUser = fbUser;
        await this.checkExistingUser();
        this.markAuthReady.forEach((cb) => cb());
        this.markAuthReady = [];
      }),
    );

    return action(() => {
      disposeUserChannel();
      unsubscribe();
      this.userChannel?.disconnect();
      this.setStatus("idle");
    });
  }

  private async checkExistingUser(): Promise<void> {
    try {
      await this.fetchUserData();
      this.setStatus("ready");
    } catch (err) {
      if (isApiError(err) && err.response?.status === 409) {
        runInAction(() => {
          this.emailClaimed = true;
        });
        this.setError(getApiErrorMessage(err));
        this.setStatus("ready");
        return;
      }
      this.setError(getApiErrorMessage(err));
      this.setStatus("error");
      if (
        err &&
        typeof err === "object" &&
        (("code" in err && (err.code === "403" || err.code === 403)) ||
          ("status" in err && err.status === 403))
      ) {
        signOut(firebaseAuth);
      }
    }
  }

  /**
   * Wait for auth onAuthStateChanged callback is finished.
   */
  async waitForAuthReady<T>(callback: () => Promise<T>): Promise<T> {
    const authReady = new Promise<void>((resolve) => {
      this.markAuthReady.push(resolve);
    });
    const result = await callback();
    await authReady;
    return result;
  }

  setStatus(status: AuthStatus): void {
    this.status = status;
  }

  setError(error: string): void {
    this.error = error;
  }

  resetStatus(): void {
    this.setStatus("idle");
    this.setError("");
  }

  sendSignInLinkToEmail(email: string): Promise<void | Record<string, never>> {
    const trackingParams = signInLinkTrackingParams();
    if (process.env.NODE_ENV !== "production") {
      const url = new URL("/finish-login", getUrlOrigin());
      for (const [key, value] of Object.entries(trackingParams)) {
        if (value) {
          url.searchParams.set(key, value);
        }
      }
      return sendSignInLinkToEmail(firebaseAuth, email, { handleCodeInApp: true, url: url.href });
    }
    return sendSignInLink({ email, trackingParams });
  }

  isSignInWithEmailLink(): boolean {
    return isSignInWithEmailLink(firebaseAuth, window.location.href);
  }

  async signInWithLink(email: string): Promise<void> {
    this.setStatus("loading");
    try {
      await this.signInWithEmailLink(email);
    } catch (err) {
      this.setError(getApiErrorMessage(err));
      this.setStatus("error");
      Sentry.captureException(err);
      throw err;
    }
  }

  async signInWithEmailLink(email: string): Promise<UserCredential> {
    return this.waitForAuthReady(async () => {
      const result = await signInWithEmailLink(firebaseAuth, email, window.location.href);
      if (!result.user) throw new Error("Authentication failed");

      runInAction(() => {
        this.fbUser = result.user;
      });
      return result;
    });
  }

  async signInWithProvider(providerName: signInProvider): Promise<UserCredential | undefined> {
    this.resetStatus();
    try {
      if (providerName === "google") {
        return await this.signInWithGoogle();
      }
    } catch (err) {
      this.setError(getApiErrorMessage(err));
      this.setStatus("error");
      Sentry.captureException(err);
    }
  }

  async signInWithGoogle(): Promise<UserCredential | undefined> {
    if (canSignInWithRedirect(firebaseAuth.config.authDomain ?? "")) {
      signInWithRedirect(firebaseAuth, googleProvider);
      return;
    }

    const authResult = await this.waitForAuthReady(async () => {
      const result = await signInWithPopup(firebaseAuth, googleProvider);
      if (!result.user) throw new Error("Authentication failed");
      return result;
    });
    await this.fetchGoogleAvatar(authResult);
    return authResult;
  }

  async signOut(reload = true): Promise<void> {
    try {
      await firebaseAuth.signOut();
      this.resetUser();
      if (reload) window.location.reload();
    } catch (err) {
      console.error("signOut error", err);
      Sentry.captureException(err);
    }
  }

  fetchGoogleAvatar(authResult: UserCredential) {
    runInAction(() => {
      this.oauthCredential = GoogleAuthProvider.credentialFromResult(authResult);
    });
    // If user is already created, update googleAccessToken to sync avatar.
    if (this.currentUser?.uid && this.oauthCredential) {
      return this.updateAndFetchUserData({
        googleAccessToken: this.oauthCredential.accessToken,
      });
    }
    return Promise.resolve(undefined);
  }

  async fetchUserData({ force = false }: { force?: boolean } = {}): Promise<
    { user?: CurrentUser } | undefined
  > {
    if (isInsideMobileWebView()) {
      if (this.userData?.uid && !force) return undefined;
    } else if (
      !this.fbUser ||
      (this.fbUser.uid && this.userData?.uid === this.fbUser.uid && !force)
    ) {
      return undefined;
    }

    const { user } = await getCurrentUser();

    runInAction(() => {
      this.userData = user ?? null;
    });

    return { user };
  }

  async updateAndFetchUserData(
    userData: UpdateCurrentUserBody,
  ): Promise<{ user?: CurrentUser } | undefined> {
    if (!this.fbUser) return undefined;

    const { user } = await updateCurrentUser(userData);

    runInAction(() => {
      this.userData = user ?? null;
    });

    return { user };
  }
}
